import React, { useState } from 'react';
import { Plus, FileText, Copy, Edit, Trash2, Search, FolderOpen, Star } from 'lucide-react';
import { PageTracker } from '../../../components/PageTracker.tsx';

const ProjectTemplates: React.FC = () => {
  const [templates, setTemplates] = useState([
    {
      id: 1,
      name: "React Web Application",
      description: "Complete setup for React app with TypeScript, Tailwind CSS, and testing",
      category: "Web Development",
      tags: ["React", "TypeScript", "Tailwind", "Jest"],
      structure: [
        "src/components/",
        "src/pages/",
        "src/hooks/",
        "src/utils/",
        "src/types/",
        "public/",
        "tests/"
      ],
      isFavorite: true,
      usageCount: 15,
      lastUsed: "2025-08-05"
    },
    {
      id: 2,
      name: "API Backend Service",
      description: "Node.js REST API with Express, MongoDB, and authentication",
      category: "Backend",
      tags: ["Node.js", "Express", "MongoDB", "JWT"],
      structure: [
        "src/controllers/",
        "src/models/",
        "src/middleware/",
        "src/routes/",
        "src/utils/",
        "config/",
        "tests/"
      ],
      isFavorite: false,
      usageCount: 8,
      lastUsed: "2025-07-28"
    },
    {
      id: 3,
      name: "Personal Learning Project",
      description: "Structure for learning new technologies and documenting progress",
      category: "Learning",
      tags: ["Documentation", "Learning", "Notes"],
      structure: [
        "docs/",
        "examples/",
        "exercises/",
        "notes/",
        "resources/",
        "projects/"
      ],
      isFavorite: true,
      usageCount: 22,
      lastUsed: "2025-08-08"
    },
    {
      id: 4,
      name: "Mobile App - React Native",
      description: "Cross-platform mobile app with navigation and state management",
      category: "Mobile",
      tags: ["React Native", "Navigation", "Redux"],
      structure: [
        "src/screens/",
        "src/components/",
        "src/navigation/",
        "src/store/",
        "src/services/",
        "assets/",
        "__tests__/"
      ],
      isFavorite: false,
      usageCount: 3,
      lastUsed: "2025-07-15"
    },
    {
      id: 5,
      name: "Data Science Project",
      description: "Python project for data analysis with Jupyter notebooks",
      category: "Data Science",
      tags: ["Python", "Jupyter", "Pandas", "Matplotlib"],
      structure: [
        "notebooks/",
        "data/raw/",
        "data/processed/",
        "src/",
        "reports/",
        "models/"
      ],
      isFavorite: false,
      usageCount: 5,
      lastUsed: "2025-07-20"
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');

  const categories = ['All', ...Array.from(new Set(templates.map(template => template.category)))];

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = filterCategory === 'All' || template.category === filterCategory;
    
    return matchesSearch && matchesCategory;
  });

  const toggleFavorite = (templateId: number) => {
    setTemplates(templates.map(template => 
      template.id === templateId ? { ...template, isFavorite: !template.isFavorite } : template
    ));
  };

  const applyTemplate = (templateId: number) => {
    setTemplates(templates.map(template => 
      template.id === templateId 
        ? { 
            ...template, 
            usageCount: template.usageCount + 1,
            lastUsed: new Date().toISOString().split('T')[0]
          } 
        : template
    ));
    // Here you would implement the actual template creation logic
    alert('Template created! (This would create the actual project structure)');
  };

  return (
    <div className="p-6 max-w-7xl mx-auto project-mgmt-page">
      <PageTracker pageTitle="Project Templates" category="project-management" />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Project Templates</h1>
        <p className="text-gray-600 text-lg">
          Jumpstart your projects with pre-configured templates. Save time on setup and focus on building 
          your ideas with standardized project structures and best practices.
        </p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700">
            <Plus size={20} />
            <span>Create Template</span>
          </button>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        
        <select 
          value={filterCategory} 
          onChange={(e) => setFilterCategory(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2"
        >
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-2">
                <FileText className="text-blue-600" size={20} />
                <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  {template.category}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => toggleFavorite(template.id)}
                  className={`${template.isFavorite ? 'text-yellow-500' : 'text-gray-300'} hover:text-yellow-500`}
                >
                  <Star size={16} fill={template.isFavorite ? 'currentColor' : 'none'} />
                </button>
                <button className="text-gray-400 hover:text-blue-600">
                  <Edit size={16} />
                </button>
                <button className="text-gray-400 hover:text-red-600">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
            <p className="text-gray-600 text-sm mb-4">{template.description}</p>
            
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FolderOpen size={14} className="mr-1" />
                Project Structure:
              </h4>
              <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                <ul className="text-xs text-gray-600 space-y-1">
                  {template.structure.map((folder, index) => (
                    <li key={index} className="font-mono">📁 {folder}</li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-1 mb-4">
              {template.tags.map((tag, index) => (
                <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  {tag}
                </span>
              ))}
            </div>
            
            <div className="flex items-center justify-between mb-4 text-xs text-gray-500">
              <span>Used {template.usageCount} times</span>
              <span>Last used: {new Date(template.lastUsed).toLocaleDateString()}</span>
            </div>
            
            <button
              onClick={() => applyTemplate(template.id)}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
            >
              <Copy size={16} />
              <span>Use Template</span>
            </button>
          </div>
        ))}
        
        {filteredTemplates.length === 0 && (
          <div className="col-span-full bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 text-center text-gray-500">
            <FileText size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium mb-2">No templates found</h3>
            <p className="text-sm">Try adjusting your search terms or create a new template!</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Total Templates</p>
              <p className="text-3xl font-bold">{templates.length}</p>
            </div>
            <FileText size={32} className="text-blue-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">Most Popular</p>
              <p className="text-xl font-bold">
                {templates.reduce((max, template) => 
                  template.usageCount > max.usageCount ? template : max
                ).name.split(' ')[0]}
              </p>
            </div>
            <Star size={32} className="text-green-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100">Favorites</p>
              <p className="text-3xl font-bold">
                {templates.filter(template => template.isFavorite).length}
              </p>
            </div>
            <Star size={32} className="text-purple-200" fill="currentColor" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100">Total Usage</p>
              <p className="text-3xl font-bold">
                {templates.reduce((total, template) => total + template.usageCount, 0)}
              </p>
            </div>
            <Copy size={32} className="text-orange-200" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectTemplates;
